import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import axios from 'axios';

const TestsList = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState({});
  const [info, setInfo] = useState('');
  const [joinStatusByTest, setJoinStatusByTest] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get('/api/tests');
        const list = data.tests || [];
        setTests(list);
        // Preload join status for each test for better UX
        try {
          const results = await Promise.allSettled(list.map(t => axios.get(`/api/tests/${t.id}`)));
          const map = {};
          results.forEach((res, idx) => {
            if (res.status === 'fulfilled') {
              const s = res.value?.data?.test?.joinStatus || null;
              map[list[idx].id] = s;
            }
          });
          setJoinStatusByTest(map);
        } catch (_) { /* ignore preload errors */ }
      } catch (e) {
        setError('Failed to load available tests');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleStartOrRequest = async (testId) => {
    setInfo('');
    setError('');
    try {
      // Fetch test details to check join status
      const { data } = await axios.get(`/api/tests/${testId}`);
      const joinStatus = data?.test?.joinStatus || null;
      if (joinStatus === 'approved') {
        navigate(`/tests/${testId}`);
        return;
      }
      if (joinStatus === 'pending') {
        setInfo('Your join request is pending approval.');
        setJoinStatusByTest(prev => ({ ...prev, [testId]: 'pending' }));
        return;
      }
      // Not requested yet, create a request
      setRequesting(prev => ({ ...prev, [testId]: true }));
      await axios.post(`/api/tests/${testId}/join-requests`);
      setInfo('Join request sent. Please wait for admin approval.');
      setJoinStatusByTest(prev => ({ ...prev, [testId]: 'pending' }));
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to process join request';
      setError(msg);
    } finally {
      setRequesting(prev => ({ ...prev, [testId]: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Available Tests
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Choose a test to get started.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {tests.length > 0 ? (
          <Grid container spacing={2}>
            {tests.map((test) => (
              <Grid item xs={12} md={6} key={test.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {test.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {test.description || 'No description'}
                    </Typography>
                    {test.duration_minutes && (
                      <Typography variant="body2" color="text.secondary">
                        Duration: {test.duration_minutes} minutes
                      </Typography>
                    )}
                    {test.instructions && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {test.instructions}
                      </Typography>
                    )}
                    {joinStatusByTest[test.id] === 'pending' && (
                      <Box sx={{ mt: 1 }}>
                        <Chip size="small" color="warning" label="Waiting for approval" />
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    {joinStatusByTest[test.id] === 'approved' ? (
                      <Button size="small" startIcon={<PlayArrow />} onClick={() => navigate(`/tests/${test.id}`)}>
                        Start Test
                      </Button>
                    ) : joinStatusByTest[test.id] === 'pending' ? (
                      <Button size="small" disabled>
                        Waiting for approval
                      </Button>
                    ) : (
                      <Button size="small" startIcon={<PlayArrow />} onClick={() => handleStartOrRequest(test.id)} disabled={!!requesting[test.id]}>
                        {requesting[test.id] ? 'Sending...' : 'Request Access'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <Typography color="text.secondary">No tests available</Typography>
          </Box>
        )}
      </Paper>
      {info && (
        <Alert severity="info" sx={{ mt: 2 }}>{info}</Alert>
      )}
    </Container>
  );
};

export default TestsList;


